import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import ExpenseCategoryDetails from "@/pages/ExpenseCategoryDetails";
import SupplierDetails from "@/pages/SupplierDetails";
import { formatPhone } from "@/lib/utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";
import { Loader2, X, FileText } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { QuickAddSupplierModal } from "./QuickAddSupplierModal";
import { QuickAddCategoryModal } from "./QuickAddCategoryModal";
import { useTenant } from "@/contexts/TenantContext";
import { DateField } from "@/components/ui/date-field";
import { format, startOfDay, endOfDay, subDays } from "date-fns";

const expenseSchema = z.object({
  expense_category_id: z.string().min(1, "Categoria é obrigatória"),
  supplier_id: z.string().optional(),
  descricao: z.string().min(1, "Descrição é obrigatória"),
  valor_total: z.number().min(0.01, "Valor deve ser maior que zero"),
  bank_account_id: z.string().min(1, "Conta bancária é obrigatória"),
  payment_method_id: z.string().min(1, "Método de pagamento é obrigatório"),
  status: z.enum(["pendente", "paga", "cancelada"]).default("paga"),
  data_vencimento: z.string().optional(),
});

type ExpenseFormData = z.infer<typeof expenseSchema>;

interface NewExpenseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction?: any;
}


export function NewExpenseModal({ open, onOpenChange, transaction, readOnly = false }: NewExpenseModalProps & { readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [expenseCategoryProfile, setExpenseCategoryProfile] = useState<{ id: string; name: string } | null>(null);
  const [supplierProfile, setSupplierProfile] = useState<{ id: string; name: string } | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);
  
  

  const form = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      expense_category_id: "",
      supplier_id: "",
      descricao: "",
      valor_total: 0,
      bank_account_id: "",
      payment_method_id: "",
      status: "paga",
      data_vencimento: "",
    },
  });

  const { data: suppliers } = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("suppliers").select("*").order("nome");
      if (error) throw error;
      return data;
    },
  });

  // Categorias de despesa (tabela existente)
  const { data: categories = [] } = useQuery({
    queryKey: ["expense-categories", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expense_categories")
        .select("id, nome, descricao, created_at")
        .eq("tenant_id", tenantId as string)
        .order("nome");
      if (error) throw error;
      return (data || []) as { id: string; nome: string; descricao?: string | null; created_at?: string | null }[];
    },
  });

  // Mapa id -> nome para buscar rapidamente o nome pelo ID
  const categoryNameById = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((cat: any) => {
      if (cat.id && cat.nome) {
        map[cat.id] = cat.nome;
      }
    });
    return map;
  }, [categories]);

  // Quick add de fornecedor agora é controlado via renderAddModal do SearchableSelect
  // Buscar contas bancárias com métodos
  const { data: bankAccountsData } = useQuery({
    queryKey: ["bank-accounts-with-methods"],
    queryFn: async () => {
      const { data: accounts, error } = await supabase
        .from("bank_accounts")
        .select(`
          id,
          nome,
          bank_accounts_methods (
            payment_method_id,
            payment_methods:payment_method_id (
              id,
              nome,
              slug
            )
          )
        `)
        .eq("status", "ativo")
        .order("nome");
      
      if (error) throw error;
      return accounts;
    },
  });

  // Criar lista de opções "Conta - Método"
  const accountMethodOptions = useMemo(() => {
    if (!bankAccountsData) return [];
    
    const options: Array<{ value: string; label: string; bankAccountId: string; paymentMethodId: string }> = [];
    
    bankAccountsData.forEach(account => {
      account.bank_accounts_methods?.forEach((bam: any) => {
        if (bam.payment_methods) {
          options.push({
            value: `${account.id}-${bam.payment_methods.id}`,
            label: `${account.nome} - ${bam.payment_methods.nome}`,
            bankAccountId: account.id,
            paymentMethodId: bam.payment_methods.id,
          });
        }
      });
    });
    
    return options;
  }, [bankAccountsData]);

  // Carregar dados da transação para edição
  useEffect(() => {
    if (transaction && open) {
      form.reset({
        expense_category_id: (transaction as any).expense_category_id || "",
        supplier_id: transaction.supplier_id || "",
        descricao: transaction.descricao || "",
        valor_total: Number(transaction.valor_total) || 0,
        bank_account_id: transaction.bank_account_id || "",
        payment_method_id: transaction.payment_method_id || "",
        status: transaction.status || "paga",
        data_vencimento: transaction.data_entrega || "",
      });

    } else if (!open) {
      // Reset quando fechar
      form.reset();
    }
  }, [transaction, open, form]);

  // Definir data atual como padrão ao abrir nova despesa
  useEffect(() => {
    if (open && !transaction) {
      const current = format(new Date(), "yyyy-MM-dd");
      const existing = form.getValues("data_vencimento");
      if (!existing) {
        form.setValue("data_vencimento", current, { shouldDirty: false, shouldTouch: false });
      }
    }
  }, [open, transaction, form]);

  const createExpenseMutation = useMutation({
    mutationFn: async (data: ExpenseFormData) => {
      const transactionData = {
        tipo: "saida" as const,
        expense_category_id: data.expense_category_id || null,
        supplier_id: data.supplier_id || null,
        descricao: data.descricao,
        valor_total: data.valor_total,
        valor_liquido: data.valor_total,
        status: data.status,
        tenant_id: tenantId,
        bank_account_id: data.bank_account_id,
        payment_method_id: data.payment_method_id,
        data_entrega: data.data_vencimento || null,
      };

      if (transaction?.id) {
        // Atualizar transação existente
        const { data: updatedTransaction, error } = await supabase
          .from("transactions")
          .update(transactionData)
          .eq("id", transaction.id)
          .select()
          .single();

        if (error) throw error;
        return updatedTransaction;
      } else {
        // Criar nova transação
        const { data: newTransaction, error } = await supabase
          .from("transactions")
          .insert([transactionData])
          .select()
          .single();

        if (error) throw error;
        return newTransaction;
      }
    },
    onSuccess: () => {
      toast.success(transaction?.id ? "Despesa atualizada com sucesso!" : "Despesa registrada com sucesso!");
      // Fechar modal primeiro, depois invalidar queries para evitar flickering
      onOpenChange(false);
      form.reset();
      // Invalidar queries após um pequeno delay para evitar interferência no fechamento
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["caixa-transactions"] });
      }, 300);
    },
    onError: (error: Error) => {
      toast.error("Erro ao registrar despesa: " + error.message);
    },
  });

  const onSubmit = (data: ExpenseFormData) => {
    createExpenseMutation.mutate(data);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        // Só permitir fechar se não estiver salvando
        if (!isOpen && !createExpenseMutation.isPending) {
          onOpenChange(isOpen);
        }
      }}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <div className="flex max-h-[90vh] flex-col">
            <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
              <DialogTitle>{readOnly ? "Visualizando Despesa" : (transaction?.id ? "Editar Despesa" : "Nova Despesa")}</DialogTitle>
              <DialogDescription className="sr-only">
                {readOnly ? "Visualize os detalhes da despesa" : (transaction?.id ? "Edite os detalhes da despesa" : "Cadastre uma nova despesa")}
              </DialogDescription>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-2 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <Form {...form}>
                <form id="expense-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="expense_category_id"
                render={({ field }) => (
                  <FormItem>
                    {readOnly && <FormLabel>Categoria *</FormLabel>}
                    <FormControl className="w-full">
                      {readOnly ? (
                        <div className="h-10 w-full rounded-md border border-input bg-muted px-3 text-sm flex items-center">
                          {categoryNameById[field.value] || ""}
                        </div>
                      ) : (
                        <SearchableSelect
                          label={
                            field.value ? (
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (field.value) {
                                          setExpenseCategoryProfile({ id: field.value, name: categoryNameById[field.value] || "categoria" });
                                        }
                                      }}
                                      className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                    >
                                      <span>Categoria *</span>
                                      <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="whitespace-pre-line z-[9999]" side="bottom" sideOffset={8} align="start">
                                    Ver ficha completa e relatórios{'\n'}da categoria <strong>{categoryNameById[field.value] || ""}</strong>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span>Categoria *</span>
                            )
                          }
                          value={field.value}
                          onChange={field.onChange}
                          options={(categories ?? []).map((c) => ({ value: c.id, label: c.nome }))}
                          placeholder="Selecione a categoria"
                          renderAddModal={({ open, onOpenChange, onCreated }) => (
                            <QuickAddCategoryModal
                              open={open}
                              onOpenChange={onOpenChange}
                              onCategoryCreated={(categoryName, categoryId) => {
                                // Usar o ID retornado ou buscar pela lista atualizada
                                if (categoryId) {
                                  form.setValue("expense_category_id", categoryId, { shouldDirty: true, shouldTouch: true });
                                  onCreated(categoryId);
                                } else {
                                  // Fallback: buscar o ID da categoria pelo nome após invalidação
                                  queryClient.invalidateQueries({ queryKey: ["expense-categories", tenantId] });
                                  setTimeout(() => {
                                    const category = categories.find((c: any) => c.nome === categoryName);
                                    if (category?.id) {
                                      form.setValue("expense_category_id", category.id, { shouldDirty: true, shouldTouch: true });
                                      onCreated(category.id);
                                    }
                                  }, 300);
                                }
                              }}
                            />
                          )}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="supplier_id"
                render={({ field }) => (
                  <FormItem>
                    {readOnly && (
                      <FormLabel>
                        {field.value ? (
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setSupplierProfile({ id: field.value, name: (suppliers ?? []).find(s => s.id === field.value)?.nome || "fornecedor" });
                                    }}
                                  className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                >
                                  <span>Fornecedor (opcional)</span>
                                  <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="whitespace-pre-line z-[9999]" side="bottom" sideOffset={8} align="start">
                                Ver ficha completa e relatório{'\n'}do fornecedor <strong>{(suppliers ?? []).find(s => s.id === field.value)?.nome || 'fornecedor'}</strong>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span>Fornecedor (opcional)</span>
                        )}
                      </FormLabel>
                    )}
                    <FormControl>
                      {readOnly ? (
                        <div className="h-10 w-full rounded-md border border-input bg-muted px-3 text-sm flex items-center">
                          {(suppliers ?? []).find((s) => s.id === field.value)?.nome || ""}
                        </div>
                      ) : (
                        <SearchableSelect
                          label={
                            field.value ? (
                              <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setSupplierProfile({ id: field.value, name: (suppliers ?? []).find(s => s.id === field.value)?.nome || "fornecedor" });
                                        }}
                                      className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                    >
                                      <span>Fornecedor (opcional)</span>
                                      <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="whitespace-pre-line z-[9999]" side="bottom" sideOffset={8} align="start">
                                    Ver ficha completa e relatório{'\n'}do fornecedor <strong>{(suppliers ?? []).find(s => s.id === field.value)?.nome || 'fornecedor'}</strong>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span>Fornecedor (opcional)</span>
                            )
                          }
                          value={field.value || ""}
                          onChange={field.onChange}
                          options={(suppliers ?? []).map((s) => ({ value: s.id, label: s.nome }))}
                          placeholder="Selecione o fornecedor"
                          renderAddModal={({ open, onOpenChange, onCreated }) => (
                            <QuickAddSupplierModal
                              open={open}
                              onOpenChange={onOpenChange}
                              onSupplierCreated={(supplier) => {
                                if (supplier?.id) {
                                  form.setValue("supplier_id", supplier.id, { shouldDirty: true, shouldTouch: true });
                                  onCreated(supplier.id);
                                  queryClient.invalidateQueries({ queryKey: ["suppliers"] });
                                }
                              }}
                            />
                          )}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              </div>

              <FormField
                control={form.control}
                name="descricao"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição *</FormLabel>
                    <FormControl>
                      {readOnly ? (
                        <div className={`w-full rounded-md border border-input bg-muted px-3 py-2 text-sm ${!field.value ? "text-muted-foreground" : ""}`}>
                          {field.value && field.value.trim().length > 0 ? field.value : "Nenhuma observação"}
                        </div>
                      ) : (
                        <Textarea {...field} rows={3} />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="valor_total"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor *</FormLabel>
                      <FormControl>
                        <MoneyInput
                          value={field.value ?? 0}
                          onValueChange={(value) => !readOnly && field.onChange(value)}
                          onBlur={field.onBlur}
                          placeholder="0,00"
                          disabled={readOnly}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              <FormField
                control={form.control}
                name="bank_account_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Conta - Método de Pagamento *</FormLabel>
                    <FormControl>
                      {readOnly ? (
                        <div className="h-10 w-full rounded-md border border-input bg-muted px-3 text-sm flex items-center">
                          {(() => {
                            const ba = form.watch("bank_account_id");
                            const pm = form.watch("payment_method_id");
                            const opt = accountMethodOptions.find(o => o.bankAccountId === ba && o.paymentMethodId === pm);
                            return opt?.label || "";
                          })()}
                        </div>
                      ) : (
                        <Select 
                          value={form.watch("bank_account_id") && form.watch("payment_method_id") 
                            ? `${form.watch("bank_account_id")}-${form.watch("payment_method_id")}` 
                            : ""
                          } 
                          onValueChange={(value) => {
                            const option = accountMethodOptions.find(opt => opt.value === value);
                            if (option) {
                              form.setValue("bank_account_id", option.bankAccountId);
                              form.setValue("payment_method_id", option.paymentMethodId);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione conta e método" />
                          </SelectTrigger>
                          <SelectContent>
                            {accountMethodOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <FormControl>
                      {readOnly ? (
                        <div className="h-10 w-full rounded-md border border-input bg-muted px-3 text-sm flex items-center">
                          {field.value === 'paga' ? 'Paga' : field.value === 'pendente' ? 'Pendente' : field.value === 'cancelada' ? 'Cancelada' : ''}
                        </div>
                      ) : (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="paga">Paga</SelectItem>
                            <SelectItem value="pendente">Pendente</SelectItem>
                            <SelectItem value="cancelada">Cancelada</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="data_vencimento"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Vencimento/Pagamento</FormLabel>
                    <FormControl>
                      <DateField {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              </div>

              </form>
            </Form>
            </div>

            <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {readOnly ? "Fechar" : "Cancelar"}
              </Button>
              {!readOnly && (
                <Button type="submit" form="expense-form" disabled={createExpenseMutation.isPending}>
                  {createExpenseMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {transaction?.id ? "Salvar Alterações" : "Registrar Despesa"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {expenseCategoryProfile && (() => {
        const cat = (categories || []).find((x: any) => x.id === expenseCategoryProfile.id);
        const infoItems = [
          cat?.descricao ? { value: cat.descricao } : null,
          cat?.created_at ? { value: `Criada em ${new Intl.DateTimeFormat('pt-BR').format(new Date(cat.created_at))}` } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!expenseCategoryProfile}
            onOpenChange={() => setExpenseCategoryProfile(null)}
            imageUrl={null}
            entityTypeLabel={"CATEGORIA DE DESPESA"}
            entityName={expenseCategoryProfile.name}
            infoItems={infoItems}
            contentClassName="px-2 pb-2 pt-1"
            showPeriodControls
            initialFrom={startOfDay(subDays(new Date(), 6))}
            initialTo={endOfDay(new Date())}
            renderContent={({ periodRange, setLoading }) => (
              <ExpenseCategoryDetails id={expenseCategoryProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}

      {supplierProfile && (() => {
        const s = (suppliers || []).find((x: any) => x.id === supplierProfile.id);
        const photo = s?.foto_url || null;
        const infoItems = [
          s?.created_at ? { value: `Fornecedor desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(s.created_at))}` } : null,
          s?.telefone ? { value: formatPhone(s.telefone as string) } : null,
          s?.email ? { value: s.email } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!supplierProfile}
            onOpenChange={() => setSupplierProfile(null)}
            imageUrl={photo || null}
            entityTypeLabel={"FORNECEDOR"}
            entityName={supplierProfile.name}
            infoItems={infoItems}
            showPeriodControls
            initialFrom={startOfDay(subDays(new Date(), 6))}
            initialTo={endOfDay(new Date())}
            renderContent={({ periodRange, setLoading }) => (
              <SupplierDetails id={supplierProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}
    </>
  );
}
